import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers } from 'lucide-react';
import SchemaModal from '../../../ui/SchemaModal';
import DeckPicker from '../../../ui/DeckPicker';
import EmptyState from '../../../ui/EmptyState';
import ButtonSpinner from '../../../ui/ButtonSpinner';
import { json } from '../../../api/apiClient';
import { errorMessage } from '../../../api/errorMessage';

/**
 * Deck-Auswahl für die Lobby: Kachel markieren, dann „Deck wählen" bestätigen. Ein Klick allein
 * wählt nicht — die Wahl ist für alle Teilnehmer sofort sichtbar, ein Fehlklick wäre ein Signal an
 * alle. `onConfirm(deckId)` liefert ein Promise; solange es läuft, zeigt der Knopf einen Spinner.
 */
export default function DeckPickerModal({ open, onOpenChange, currentDeckId, onConfirm }) {
  const navigate = useNavigate();
  const [decks, setDecks] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pending, setPending] = useState(currentDeckId ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Nur an `open` (und reloadKey) gebunden: currentDeckId ändert sich während der Bestätigung
  // durch das Nachladen der Lobby — ein Neuladen der Liste in dem Moment ließe das Grid flackern.
  useEffect(() => {
    if (!open) return;
    setPending(currentDeckId ?? null);
    setLoadError(null);
    setDecks(null);
    let alive = true;
    json('/api/decks/list')
      .then((resp) => { if (alive) setDecks(Array.isArray(resp) ? resp : (resp?.decks || [])); })
      .catch((err) => { if (alive) setLoadError(err); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reloadKey]);

  const canConfirm = !!pending && pending !== currentDeckId && !submitting;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm(pending);
      onOpenChange(false);
    } catch (err) {
      // Der Aufrufer meldet den Fehler (Toast); das Modal bleibt offen. Ein nicht (mehr) eigenes
      // Deck heißt: die Liste ist veraltet — neu laden.
      if (err?.code === 'DECK_NOT_OWNED') setReloadKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SchemaModal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Deck wählen"
      description="Markiere ein Deck und bestätige die Auswahl."
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={() => onOpenChange(false)} disabled={submitting}>Abbrechen</button>
          <button type="button" className="btn-primary" onClick={handleConfirm} disabled={!canConfirm}>
            {submitting ? <><ButtonSpinner /> Wird gewählt…</> : 'Deck wählen'}
          </button>
        </>
      }
    >
      <DeckPicker
        decks={decks}
        error={loadError}
        errorText={loadError ? errorMessage(loadError, 'Decks konnten nicht geladen werden.') : undefined}
        onRetry={() => setReloadKey((k) => k + 1)}
        selectedId={pending}
        onSelect={setPending}
        autoFocusSearch
        emptyState={
          <EmptyState
            icon={Layers}
            title="Du hast noch kein Deck"
            description="Lege zuerst ein Deck an, dann kannst du es hier für den Playtest wählen."
            action={<button type="button" className="btn-primary" onClick={() => navigate('/decks')}>Zu den Decks</button>}
          />
        }
      />
    </SchemaModal>
  );
}
